import platform from '../config/platform.ts';
import {authBrokerCallbackUrl} from '../config/authBroker.ts';

/**
 * FNP-864. Single enforcement point for redirect-target validation.
 *
 * The portal hands raw Cognito tokens to other platform apps in a URL
 * fragment, so the redirect target is the only thing standing between a
 * signed-in user and an attacker-controlled origin receiving their tokens.
 *
 * Every site that builds such a URL MUST call this, and MUST call it on the
 * value it is about to use — not on the value it once validated. Cached
 * values are untrusted input: the cache is written before the user has
 * authenticated, so an unauthenticated visitor can seed it.
 *
 * Matching is an EXACT string comparison against the known app origins in
 * `config/platform.ts`. Deliberately not `startsWith`, `includes`, or any
 * prefix/substring test — those admit `https://portal.flexi.network.evil.com`
 * and `https://evil.com?x=https://portal.flexi.network`.
 */
export const isAllowedRedirectUrl = (
    url: unknown,
    responseType?: string | null
): url is string => {
    if (typeof url !== 'string' || url === '') {
        return false;
    }

    // FNP-871/872: the auth broker's callback is a legitimate redirect target.
    //
    // Under the code flow the portal does not redirect to the APP — it
    // redirects back to the broker, which restores the app's own redirect_uri
    // and state from the request it parked. So the broker's callback is the
    // only target the portal ever sees on that path.
    //
    // Still an EXACT match against a single configured value, not a relaxation
    // of the rule: `authBrokerCallbackUrl` is built from the per-stage broker
    // endpoint in config, never from anything the caller supplies.
    //
    // CODE PATH ONLY. The fragment relay would put raw tokens in the callback's
    // URL, and a redirect from the broker would carry that fragment on to
    // wherever the broker sends the browser next.
    if (authBrokerCallbackUrl !== '' && url === authBrokerCallbackUrl) {
        return responseType === 'code';
    }

    return Object.values(platform).includes(url);
};
